import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { api, handleApiError } from '../lib/api';
import Logo from './common/Logo';

type ResetPasswordErrors = Record<string, string>;

const WellsiteResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState(token ? 'reset' : 'request'); // 'request' or 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<ResetPasswordErrors>({});
  const [successMessage, setSuccessMessage] = useState('');
  // Track validity of the reset token. `null` means validation has not yet run.
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);

  // Validate token on component mount if present
  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token]);

  const validateToken = async () => {
    try {
      setIsLoading(true);
      // Assuming you have an endpoint to validate reset tokens
      await api.auth.validateResetToken(token);
      setTokenValid(true);
    } catch (error) {
      console.error('Token validation error:', error);
      setTokenValid(false);
      setErrors({ general: 'Invalid or expired reset token. Please request a new password reset.' });
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {} as ResetPasswordErrors;

    if (step === 'request') {
      // Email validation for reset request
      if (!email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        newErrors.email = 'Email is invalid';
      }
    } else if (step === 'reset') {
      // Password validation for reset
      if (!password) {
        newErrors.password = 'Password is required';
      } else if (password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
        newErrors.password = 'Password must contain uppercase, lowercase, and number';
      }

      // Confirm password validation
      if (!confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRequestReset = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      await api.auth.requestPasswordReset({ email: email.trim() });
      setSuccessMessage('Password reset instructions have been sent to your email. Please check your inbox and follow the link to reset your password.');
      setEmail(''); // Clear email for security
    } catch (err: any) {
      console.error('Password reset request error:', err);
      const errorMessage = handleApiError(err);

      if (err.data?.errors && Array.isArray(err.data.errors)) {
        const errorMap = {} as ResetPasswordErrors;
        err.data.errors.forEach((errorItem: any) => {
          errorMap[errorItem.path || 'general'] = errorItem.msg || errorItem.message;
        });
        setErrors(errorMap);
      } else {
        setErrors({ general: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      await api.auth.resetPassword({
        token,
        password,
        confirmPassword
      });
      
      setSuccessMessage('Password reset successfully! Redirecting to login...');
      
      // Redirect to login after success
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);
      
    } catch (err: any) {
      console.error('Password reset error:', err);
      const errorMessage = handleApiError(err);

      if (err.data?.errors && Array.isArray(err.data.errors)) {
        const errorMap = {} as ResetPasswordErrors;
        err.data.errors.forEach((errorItem: any) => {
          errorMap[errorItem.path || 'general'] = errorItem.msg || errorItem.message;
        });
        setErrors(errorMap);
      } else {
        setErrors({ general: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const renderError = (field: keyof ResetPasswordErrors) => {
    if (errors[field]) {
      return (
        <div className="flex items-center space-x-1 mt-1">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-sm text-red-400">{errors[field]}</span>
        </div>
      );
    }
    return null;
  };

  // Show loading state while validating token
  if (token && tokenValid === null) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Validating reset token...</p>
        </div>
      </div>
    );
  }

  // Show error if token is invalid
  if (token && tokenValid === false) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <div className="relative z-10 p-6">
          <Logo className="h-6 w-auto" />
        </div>

        <div className="relative z-10 flex-1 flex items-center justify-center px-6">
          <div className="w-full max-w-md text-center">
            <div className="bg-red-900/50 border border-red-700 rounded-lg p-6 mb-6">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Invalid Reset Link</h2>
              <p className="text-red-200 mb-4">
                This password reset link is invalid or has expired. Please request a new password reset.
              </p>
              <button
                onClick={() => {
                  setStep('request');
                  setTokenValid(null);
                  setErrors({});
                }}
                className="w-full px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Request New Reset
              </button>
            </div>
            
            <button
              onClick={handleBackToLogin}
              className="text-white hover:text-gray-300 transition-colors font-medium"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #ffffff 1px, transparent 1px),
                           radial-gradient(circle at 75% 75%, #ffffff 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Header */}
      <div className="relative z-10 p-6">
        <div className="flex items-center justify-between">
          <Logo className="h-6 w-auto" />
          <button
            onClick={handleBackToLogin}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Login</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-6">
              {step === 'request' ? 'Reset Password' : 'Set New Password'}
            </h2>
            {successMessage && (
              <div className="flex items-center justify-center space-x-2 bg-green-900/50 border border-green-700 rounded-lg p-3 mb-4">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400">{successMessage}</span>
              </div>
            )}
            {errors.general && (
              <div className="flex items-center justify-center space-x-2 bg-red-900/50 border border-red-700 rounded-lg p-3 mb-4">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-400">{errors.general}</span>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6">
            {step === 'request' ? (
              // Password Reset Request Form
              <form onSubmit={handleRequestReset}>
                <div className="space-y-4">
                  <div className="mb-4">
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Enter your email address and we'll send you a link.
                    </p>
                  </div>

                  <div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full px-4 py-3 bg-black border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white transition-colors ${
                        errors.email ? 'border-red-500' : 'border-gray-600'
                      }`}
                      placeholder=""
                      required
                      disabled={isLoading}
                    />
                    {renderError('email')}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full py-3 rounded-lg font-medium transition-all duration-200 ${
                      isLoading
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-black hover:bg-gray-200 hover:shadow-lg transform hover:scale-[1.02]'
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending Reset Email...</span>
                      </div>
                    ) : (
                      'Continue'
                    )}
                  </button>
                </div>
              </form>
            ) : (
              // New Password Form
              <form onSubmit={handleResetPassword}>
                <div className="space-y-4">
                  <div className="mb-4">
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Enter your new password. Make sure it's strong and secure.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`w-full px-4 py-3 pr-12 bg-black border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white transition-colors ${
                          errors.password ? 'border-red-500' : 'border-gray-600'
                        }`}
                        placeholder="Create a secure password"
                        required
                        minLength={8}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-700 rounded transition-colors"
                        disabled={isLoading}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5 text-gray-400" />
                        ) : (
                          <Eye className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {renderError('password')}
                    {!errors.password && (
                      <p className="text-xs text-gray-500 mt-1">
                        Minimum 8 characters with uppercase, lowercase, and number
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`w-full px-4 py-3 pr-12 bg-black border rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-white transition-colors ${
                          errors.confirmPassword ? 'border-red-500' : 'border-gray-600'
                        }`}
                        placeholder="Confirm your new password"
                        required
                        minLength={8}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={toggleConfirmPasswordVisibility}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-700 rounded transition-colors"
                        disabled={isLoading}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5 text-gray-400" />
                        ) : (
                          <Eye className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {renderError('confirmPassword')}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full py-3 rounded-lg font-medium transition-all duration-200 ${
                      isLoading
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-black hover:bg-gray-200 hover:shadow-lg transform hover:scale-[1.02]'
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        <span>Resetting Password...</span>
                      </div>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Additional Actions */}
            <div className="mt-6 text-center">
              {step === 'request' && (
                <p className="text-gray-400">
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="text-white font-medium hover:underline transition-colors"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </p>
              )}
              
              {step === 'reset' && !successMessage && (
                <button
                  type="button"
                  onClick={() => {
                    setStep('request');
                    setPassword('');
                    setConfirmPassword('');
                    setErrors({});
                  }}
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                  disabled={isLoading}
                >
                  Need to use a different email?
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 p-6 text-center">
        <p className="text-gray-500 text-sm">
          © 2025 Wellsite. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default WellsiteResetPassword;
